// Navbar.js — Wave 1 shell component.
//
// The Navbar no longer renders its own standalone <nav> bar; the AppLayout
// header owns the structure. Navbar now exposes three collaboration points:
//   - Navbar.getNavItems({ isLoggedIn, identity }) — shared nav model used by
//     both desktop nav and the mobile drawer (same items, same permissions);
//   - Navbar.isBellVisibleForPath(path) — where the bell is hidden;
//   - <Navbar mode="brand" /> / <Navbar mode="auth" /> — brand block and
//     login/logout button used inside the header / drawer footer.
//
// Role gating: prefers server-confirmed identity (useMyIdentity, M3). The
// legacy localStorage role is used ONLY as a fallback while identity is still
// resolving, preserving the exact master-clerk shortcut visibility rules.
// Navigation permissions and destinations are unchanged.

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ScanLine, Search, Video, LogIn, LogOut, FileText, Home } from "lucide-react";
import { useMyIdentity } from "../utils/identity";
import { getSessionLandingRoute } from "../utils/dashboardRoute";

const MASTER_CLERK_ROLES = new Set([
  "CLERK",
  "CHIEF CLERK",
  "DORCUS CLERK",
  "COMCEN",
  "MASTER CLERK",
]);

// Where the notification bell is hidden (public/auth surfaces).
const BELL_HIDDEN_PATHS = ["/login", "/", "/register", "/admin"];

const isMasterClerkPath = (path) =>
  path.includes("/dashboard/master-clerk") ||
  path.includes("/dashboard/clerk/") ||
  path.includes("/dashboard/chief-clerk/") ||
  path.includes("/dashboard/soi/n-a/chief-clerk") ||
  path.includes("/dashboard/soi/n-a/dorcus-clerk") ||
  path.includes("/dashboard/soi/n-a/comcen") ||
  /\/dashboard\/.+\/clerk$/.test(path);
// Retained for dashboard-role parity checks; the tab model now derives
// master-clerk shortcut visibility from the role set above.
void isMasterClerkPath;

// Shared nav model — one source for desktop AND mobile drawer.
Navbar.getNavItems = ({ isLoggedIn = false, identity = null, fallbackRole = "", homeRoute = "/" } = {}) => {
  if (!isLoggedIn) {
    return [
      { to: "/", label: "Home" },
      { to: "/meeting", label: "Meeting Hub", icon: Video },
      { to: "/scan", label: "Scan", icon: ScanLine },
    ];
  }
  const role = String(identity?.role || fallbackRole || "").trim().toUpperCase();
  const hideMasterClerkShortcuts = MASTER_CLERK_ROLES.has(role);
  const items = [{ to: homeRoute, label: "Home", icon: Home }];
  if (!hideMasterClerkShortcuts) items.push({ to: "/inbox", label: "Inbox" });
  if (!hideMasterClerkShortcuts) items.push({ to: "/search", label: "Search", icon: Search });
  // Document Studio — its own workspace, visible to every signed-in user.
  items.push({ to: "/studio", label: "Document Studio", icon: FileText });
  items.push({ to: "/meeting", label: "Meeting Hub", icon: Video });
  items.push({ to: "/scan", label: "Scan", icon: ScanLine });
  return items;
};

Navbar.isBellVisibleForPath = (path) => !BELL_HIDDEN_PATHS.includes(String(path || ""));

function Navbar({ mode = "brand", onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { identity } = useMyIdentity();
  const [sessionToken, setSessionToken] = useState(false);

  useEffect(() => {
    const sync = () =>
      setSessionToken(
        Boolean(
          localStorage.getItem("authToken") ||
            sessionStorage.getItem("authToken") ||
            localStorage.getItem("adminToken")
        )
      );
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [location.pathname]);

  const isLoggedIn = Boolean(identity) || (!identity && sessionToken);
  const homeRoute = getSessionLandingRoute(identity);

  const handleAuthClick = () => {
    if (isLoggedIn) {
      localStorage.clear();
      sessionStorage.clear();
      setSessionToken(false);
      navigate("/login");
    } else {
      navigate("/login");
    }
    onNavigate?.();
  };

  if (mode === "brand") {
    return (
      <button type="button" className="wv-header__brand" onClick={() => { navigate(homeRoute); onNavigate?.(); }} aria-label="WAVY home">
        <span className="wv-header__mark" aria-hidden="true">W</span>
        <span className="wv-header__brand-text">
          <span className="wv-header__title">HQ KA WAVEY</span>
          <span className="wv-header__subtitle">Paperless Command Workflow</span>
        </span>
      </button>
    );
  }

  // mode === "auth"
  return (
    <button
      type="button"
      className={`wv-header__btn ${isLoggedIn ? "" : "wv-header__btn--primary"}`.trim()}
      onClick={handleAuthClick}
    >
      {isLoggedIn ? <LogOut size={15} aria-hidden="true" /> : <LogIn size={15} aria-hidden="true" />}
      {isLoggedIn ? "Logout" : "Login"}
    </button>
  );
}

export default Navbar;
